import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Forum as CommunicationIcon
} from '@mui/icons-material';

import { useTranslation } from 'react-i18next';

import useRoadmapStore from '../store/roadmapStore';
import { STEP_LABELS } from '../utils/constants';
import PageContainer from '../components/PageContainer';
import StepPageHeader from '../components/StepPageHeader';
import useAISuggest from '../hooks/useAISuggest';

export default function GovernancePage() {
  const { 
    getStepData, 
    updateStepData, 
    markStepComplete, 
    isStepCompleted 
  } = useRoadmapStore();
  const { t } = useTranslation();
  const handleAISuggest = useAISuggest('governance');
  const _storeStep = useRoadmapStore(s => s.roadmapData?.steps?.governance);

  const stepData = getStepData('governance');
  const [decisionMakers, setDecisionMakers] = useState(stepData.decision_makers || []);
  const [communicationPlan, setCommunicationPlan] = useState(stepData.communication_plan || '');
  const [isComplete, setIsComplete] = useState(isStepCompleted('governance'));
  const [newDecisionMaker, setNewDecisionMaker] = useState({ name: '', role: '', responsibility: '' });

  useEffect(() => {
    const data = getStepData('governance');
    setDecisionMakers(data.decision_makers || []);
    setCommunicationPlan(data.communication_plan || '');
    setIsComplete(isStepCompleted('governance'));
  }, [getStepData, isStepCompleted, _storeStep]);

  const updateData = () => {
    updateStepData('governance', {
      decision_makers: decisionMakers,
      communication_plan: communicationPlan,
      responsibility_matrix: {} // Could be expanded later
    });
  };

  const handleAddDecisionMaker = () => {
    if (newDecisionMaker.name.trim() && newDecisionMaker.role.trim()) {
      const updated = [...decisionMakers, newDecisionMaker];
      setDecisionMakers(updated);
      setNewDecisionMaker({ name: '', role: '', responsibility: '' });
      updateStepData('governance', {
        decision_makers: updated,
        communication_plan: communicationPlan,
        responsibility_matrix: {}
      });
    }
  };

  const handleDeleteDecisionMaker = (index) => {
    const updated = decisionMakers.filter((_, i) => i !== index);
    setDecisionMakers(updated);
    updateStepData('governance', {
      decision_makers: updated,
      communication_plan: communicationPlan,
      responsibility_matrix: {}
    });
  };

  const handleSaveCommunicationPlan = () => {
    updateData();
  };

  const _saveRef = useRef(null);
  useEffect(() => { _saveRef.current = handleSaveCommunicationPlan; });
  useEffect(() => () => { if (_saveRef.current) _saveRef.current(); }, []); // eslint-disable-line

  const handleCompleteToggle = () => {
    const newCompleteState = !isComplete;
    const success = markStepComplete('governance', newCompleteState);
    setIsComplete(success);
  };

  const canComplete = decisionMakers.length > 0;

  return (
    <PageContainer maxWidth={1000}>
      <StepPageHeader
        stepNum={8}
        title={t(STEP_LABELS.governance)}
        intro={t('stepContent.governance.intro')}
        isComplete={isComplete}
        canComplete={canComplete}
        onToggleComplete={handleCompleteToggle}
        requireMessage={t('stepContent.governance.alerts.requireStructure')}
      onAISuggest={handleAISuggest}
        />

      {/* Decision Makers */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon />
          {t('stepContent.governance.sections.structure.title')}
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('stepContent.governance.form.fields.name')}
              value={newDecisionMaker.name}
              onChange={(e) => setNewDecisionMaker({ ...newDecisionMaker, name: e.target.value })}
              placeholder={t('stepContent.governance.form.fields.name')}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label={t('stepContent.governance.form.fields.description')}
              value={newDecisionMaker.role}
              onChange={(e) => setNewDecisionMaker({ ...newDecisionMaker, role: e.target.value })}
              placeholder={t('stepContent.governance.form.fields.description')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('stepContent.governance.form.fields.responsibilities')}
              value={newDecisionMaker.responsibility}
              onChange={(e) => setNewDecisionMaker({ ...newDecisionMaker, responsibility: e.target.value })}
              placeholder={t('stepContent.governance.form.fields.responsibilities')}
            />
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAddDecisionMaker}
              disabled={!newDecisionMaker.name.trim() || !newDecisionMaker.role.trim()}
              sx={{ height: '40px' }}
            >
              <AddIcon />
            </Button>
          </Grid>
        </Grid>

        <List>
          {decisionMakers.map((person, index) => (
            <ListItem
              key={index}
              sx={{ bgcolor: 'grey.50', mb: 1, borderRadius: 1 }}
              secondaryAction={
                <IconButton onClick={() => handleDeleteDecisionMaker(index)}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText
                primary={`${person.name} - ${person.role}`}
                secondary={person.responsibility}
              />
            </ListItem>
          ))}
        </List>

        {decisionMakers.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
            {t('stepContent.governance.empty.title')}
          </Typography>
        )}
      </Paper>

      {/* Communication Plan */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CommunicationIcon />
          Communication Plan
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('stepContent.governance.sections.processes.description')}
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={8}
          label={t('stepContent.governance.form.fields.description')}
          value={communicationPlan}
          onChange={(e) => setCommunicationPlan(e.target.value)}
          onBlur={handleSaveCommunicationPlan}
          placeholder={t('stepContent.governance.form.fields.description')}
          sx={{ mb: 3 }}
        />

        <Button
          variant="contained"
          onClick={handleSaveCommunicationPlan}
        >
          {t('common.save')}
        </Button>
      </Paper>
    </PageContainer>
  );
}
